'use client';

import { useTranslations } from 'next-intl';
import { createElement, useMemo } from 'react';
import { VipIcon } from '@/app/components/icons/VipIcon';
import type { SelectOption } from '@/app/components/shared/CommonSelect';
import { isHevcSupported } from '@/app/utils/client';

export type CodecOption = SelectOption;
export type CodecOptionsResult = {
  defaultValue: 'h265' | 'h264';
  isHevcSupported: boolean;
  options: CodecOption[];
};

const createCodecOptions = (t: ReturnType<typeof useTranslations>, isSupported: boolean): CodecOption[] => {
  const allOptions: CodecOption[] = [
    {
      value: 'h265',
      label: t('create.createForm.settings.codec.optionH265.text'),
    },
    {
      value: 'h264',
      label: t('create.createForm.settings.codec.optionH264.text'),
    },
    {
      value: 'h265_pro',
      label: t('create.createForm.settings.codec.optionH265Pro.text'),
      badge: { content: createElement(VipIcon, { className: 'mx-0.5 w-3' }) },
    },
    {
      value: 'h264_pro',
      label: t('create.createForm.settings.codec.optionH264Pro.text'),
      badge: { content: createElement(VipIcon, { className: 'mx-0.5 w-3' }) },
    },
  ];

  if (isSupported) {
    return allOptions;
  }

  return allOptions.filter((option) => option.value.startsWith('h264'));
};

export const useCodecOptions = () => {
  const t = useTranslations();
  const supported = useMemo(() => isHevcSupported(), []);

  const options = useMemo(() => createCodecOptions(t, supported), [supported, t]);
  const defaultValue = supported ? 'h265' : 'h264';

  return {
    defaultValue,
    isHevcSupported: supported,
    options,
  } satisfies CodecOptionsResult;
};
